import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, UntypedFormControl } from '@angular/forms';
import {
  StringCustomFieldConfig,
  SharedModule,
  FormInputComponent,
  NotificationService,
  DynamicFormInputComponent,
  DataService
} from '@vendure/admin-ui/core';
import { TopReviewsData, defaultTopReviewsData } from '../ui-types';
import { GET_PRODUCT_REVIEW } from '../graphql';
import { areArraysEqual, notNullOrUndefined, safeJSONParse } from '../utils';
import { ProductReview } from '../generated';
import { take } from 'rxjs/operators';

@Component({
  template: `
    <vdr-card>
      <vdr-form-field [label]="'Reviews'">
        <vdr-dynamic-form-input #reviewsInput
            [control]="reviewsControl"
            [def]="{
                type: 'relation',
                entity: 'ProductReview',
                list: true,
                ui: {
                    component: 'product-review-selector'
                }
            }"
        >
        </vdr-dynamic-form-input>
      </vdr-form-field>
    </vdr-card>
  `,
  standalone: true,
  imports: [SharedModule],
})
export class TopReviewsFormComponent
  implements FormInputComponent<StringCustomFieldConfig>, OnInit, AfterViewInit
{
  @ViewChild('reviewsInput') reviewsInput: DynamicFormInputComponent;

  readonly: boolean;
  config: StringCustomFieldConfig;
  formControl: FormControl;

  reviewsControl = new UntypedFormControl([]);

  topReviewsData: TopReviewsData;

  constructor(
    private changeDetector: ChangeDetectorRef,
    private dataService: DataService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.topReviewsData = safeJSONParse<TopReviewsData>(
      this.formControl.value,
      this.notificationService,
      defaultTopReviewsData,
    )!;
  }

  async ngAfterViewInit() {
    if(this.reviewsInput) {
      this.reviewsInput.registerOnChange(this.onReviewsChange);
    }
    if(this.reviewsInput && this.topReviewsData?.reviewIds?.length) {
      const reviewList = await Promise.all(
        this.topReviewsData.reviewIds.map((id: any) => this.fetchProductReview(id))
      );
      this.reviewsInput.writeValue(reviewList.filter(notNullOrUndefined) || []);
      this.reviewsControl.setValue(reviewList.filter(notNullOrUndefined) || []);
      this.changeDetector.markForCheck();
    }
  }

  private async fetchProductReview(productReviewId: string): Promise<any> {
    try {
      const productReviewFetcher$ = this.dataService
        .query<{ productReview: ProductReview }>(
          GET_PRODUCT_REVIEW,
          { productReviewId: productReviewId }
        )
        .mapSingle((data: any) => data.productReview)
        .pipe(take(1));
      const data = await productReviewFetcher$.toPromise();
      return data;
    } catch (error) {
      console.error('Error fetching product review:', error);
      this.notificationService.error(`Error fetching product review ${productReviewId}: ${error}`);
      return null;
    }
  }

  private onReviewsChange = (values: any) => {
    if (this.topReviewsData) {
      const newIds = values.map((value: any) => value.id);
      if (!areArraysEqual(this.topReviewsData.reviewIds || [], newIds)) {
        this.topReviewsData.reviewIds = newIds;
        this.updateJsonString();
      }
    }
  };

  private updateJsonString() {
    const jsonString = JSON.stringify(this.topReviewsData);
    this.formControl.setValue(jsonString);
    this.formControl.markAsDirty();
  }
}
